import React, { useCallback, useEffect, useState } from 'react'

import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { useApi, useDashboard } from '@/hooks'
import AssetHistoryChart from '@/insights/assets/historyGraph/AssetHistoryChart'
import type { AssetHistory, TempUnits } from '@/insights/assets/historyGraph/types'
import { buildQuery } from '@/insights/common/queryUtils'

import GraphPanelShell from './GraphPanelShell'

import { ALERT_ACK_HIDDEN_SERIES } from '../chartDefaults'
import type { GraphPanelProps } from '../types'

const CHART_PERIOD_HOURS = 24

/** Temperature telemetry for the acknowledged alert's asset — the default panel,
 *  used for every alert category except fuel. */
const TemperatureGraphPanel: React.FC<GraphPanelProps> = ({
  assetId,
  companyId,
  chartFrom,
  alertMarkers,
  className,
}) => {
  const { api } = useApi()
  const units = (useDashboard().configuration.units as TempUnits) ?? 'Metric'

  const [data, setData] = useState<AssetHistory>()
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const fetchWindow = useCallback(
    async (window: HistoryWindow): Promise<AssetHistory | undefined> => {
      const query = buildQuery({
        companyId,
        date: 'custom',
        from: window.from.toISO() || '',
        periodHours: String(window.to.diff(window.from, 'hours').hours),
      })

      return api.simpleGet<AssetHistory>({
        path: `/assets/history/${assetId}?${query}`,
        origin: 'TemperatureGraphPanel#fetchWindow',
      })
    },
    [api, assetId, companyId]
  )

  const reload = useCallback(
    // IMPORTANT: don't push the result back into `data` — the chart owns its window
    // internally, and changing the `data` prop here would make it treat the fetch as a
    // reconfigure and discard the user's range.
    async (_: unknown, window: HistoryWindow): Promise<AssetHistory | undefined> => {
      try {
        return await fetchWindow(window)
      } catch {
        return undefined
      }
    },
    [fetchWindow]
  )

  useEffect(() => {
    let isActive = true

    const load = async () => {
      try {
        setIsLoading(true)
        setError('')
        const response = await fetchWindow({
          from: chartFrom,
          to: chartFrom.plus({ hours: CHART_PERIOD_HOURS }),
        })
        if (isActive) setData(response)
      } catch {
        if (isActive) {
          setData(undefined)
          setError('Unable to load telemetry graph data.')
        }
      } finally {
        if (isActive) setIsLoading(false)
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [fetchWindow, chartFrom])

  return (
    <GraphPanelShell
      title="Temperature Graph"
      className={className}
      error={error}
      isLoading={isLoading}
      hasData={!!data}
      emptyMessage="No telemetry data found for the selected alert window."
    >
      {(chartHeight) => (
        <AssetHistoryChart
          data={data as AssetHistory}
          from={chartFrom}
          periodHours={CHART_PERIOD_HOURS}
          alertMarkers={alertMarkers}
          reload={reload}
          units={units}
          chartHeight={chartHeight}
          defaultHidden={ALERT_ACK_HIDDEN_SERIES}
        />
      )}
    </GraphPanelShell>
  )
}

export default TemperatureGraphPanel
